import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { IVideoKycScreen, TimeObject } from '../../utils/interface';
import { CommonButton, StepHeader, ModalFactory, Timer } from '@Components';
import {
  VIDEO_KYC,
  NIUM_FOREX_CURRENT_SCREEN,
  totalSteps,
  screenSettings
} from '../../utils/constants';
import { Col, FormContainer, ParentContainer, Row, DisclaimerInfo } from '../../styles';
import { CLOSE_POPUP_ICON, CURRENT_LOCATION_PIN_ICON, ID_CARD_FILL_ICON } from '@Images';
import { calculateRemainingTime } from '@Utils/library';
import CancelPaymentModal from '../../utils/cancel-payment-modal';
import { NF_SCREENS, NF_CALLBACK_URLS } from '../../root/screenConstants';
import {
  getVideoKycCaptureLink,
  getUserVideoKycStatus,
  changeNiumForexScreen,
  getNiumForexTransactionDetails,
  storeTransactionIdAction,
  getNiumForexUserDetails
} from '@Redux/actions/nium-forex';
import { IStore } from '../../../../store.type';
import { activateLoader, disableLoader } from '@Redux/actions/ui-trigger';
import {
  VERIFIED,
  FLOW_TYPE,
  REJECTED,
  REVIEW_REQUIRED,
  IN_PROGRESS,
  NF_IS_VKYC_MODAL_CLOSED
} from '@Utils/constants';
import RetryVideoKycModal from '../aadhar-verification/retry-aadhar-verification-modal';
import { NF_VIDEO_KYC_CT } from '../../../../utils/constants/eventsConstants';
import { Text, TimeContainer, Label, Value } from './style';
import { logEvent } from '../../../../utils/events/logUserEvents';
import { LOG_TYPE } from '../../../../utils/events/eventsConstants';

const VideoKycScreen: React.FC<IVideoKycScreen> = (props) => {
  const {
    activateLoader,
    disableLoader,
    getVideoKycCaptureLink,
    changeNiumForexScreen,
    getUserVideoKycStatus,
    transactionId,
    dispatch,
    getNiumForexTransactionDetails,
    storeTransactionIdAction,
    isReferral
  } = props;

  const [txnId, setTxnId] = useState<number>(transactionId);
  const [timeRemaining, setTimeRemaining] = useState<TimeObject>({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const currentURL = window.location.href;
  const stepperData = screenSettings[NF_SCREENS.video_kyc as keyof typeof screenSettings];

  useEffect(() => {
    if (txnId) onTxnId();
    //eslint-disable-next-line
  }, [txnId]);

  const onTxnId = async () => {
    sessionStorage.setItem(NF_IS_VKYC_MODAL_CLOSED, 'true');
    await handleGetTransactionDetails();
    const isUserInitiatedVideoKycFlag =
      localStorage.getItem(NIUM_FOREX_CURRENT_SCREEN) === NF_SCREENS.video_kyc;
    const videoKycUrl = currentURL.split('/').includes(NF_CALLBACK_URLS.videoKycCallbackUrl);
    const triggerGetVideoKycStatusFlag = isUserInitiatedVideoKycFlag && videoKycUrl;
    if (triggerGetVideoKycStatusFlag) {
      dispatch(activateLoader());
      setTimeout(() => {
        handleGetVideoKycStatus();
      }, 8000);
    }
  };

  useEffect(() => {
    if (isReferral) {
      setTxnId(transactionId);
    } else {
      //eslint-disable-next-line
      const match = currentURL.match(/\/(\d+)/);
      if (match) {
        const urlTransactionId = match[1];
        setTxnId(parseInt(urlTransactionId));
      }
    }
    //eslint-disable-next-line
  }, []);

  const logEvents = (): void => {
    const ctData = {
      pageName: NF_SCREENS.video_kyc
    };
    logEvent(NF_VIDEO_KYC_CT, FLOW_TYPE.TRANSACTION, ctData, LOG_TYPE.CLEVER_TAP);
  };

  const handleGetCaptureLink = async () => {
    if (txnId) {
      logEvents();
      const payload = {
        transactionId: txnId
      };
      const res: any = await dispatch(getVideoKycCaptureLink(payload));
      localStorage.setItem(NIUM_FOREX_CURRENT_SCREEN, NF_SCREENS.video_kyc);
      window.open(decodeURIComponent(res?.captureLink || ''), '_self');
    }
  };

  const handleStartVideoKyc = () => {
    localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
    dispatch(activateLoader());
    handleGetCaptureLink();
    dispatch(disableLoader());
  };

  const handleCloseVideoKyc = async () => {
    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <CancelPaymentModal
          onConfirm={() => {
            onClose(true);
            dispatch(changeNiumForexScreen(NF_SCREENS.send_money));
          }}
          onContinue={() => onClose(true)}
          postPan={true}
        />
      ),
      'nium-forex cancel-vKyc center-modal insta-modal',
      true
    );
  };

  const handleIssueWithVideoKycVerification = async (status: string, isModalClosed = false) => {
    const isVideoKYCRejected = status === REJECTED;
    const isVideoKYCInProgress = status === IN_PROGRESS;
    const title = isVideoKYCInProgress
      ? VIDEO_KYC?.videoKycInProgress?.heading
      : isVideoKYCRejected
      ? VIDEO_KYC?.videoKycRejection?.heading
      : VIDEO_KYC?.videoKycFailure?.heading;
    const description = isVideoKYCInProgress
      ? VIDEO_KYC?.videoKycInProgress?.subHeading
      : isVideoKYCRejected
      ? VIDEO_KYC?.videoKycRejection?.subHeading
      : VIDEO_KYC?.videoKycFailure?.subHeading;
    const buttonText = isVideoKYCInProgress
      ? VIDEO_KYC?.videoKycInProgress?.gotIt
      : isVideoKYCRejected
      ? VIDEO_KYC?.videoKycRejection?.startOver
      : VIDEO_KYC?.videoKycFailure?.tryAgainVideoKyc;

    // @ts-ignore
    return await ModalFactory.builder?.(
      (onClose: any) => (
        <RetryVideoKycModal
          headingText={title}
          subHeadingText={description}
          submitButtonText={buttonText}
          handleTryAgain={() => {
            localStorage.removeItem(NIUM_FOREX_CURRENT_SCREEN);
            onClose(true);
            if (isVideoKYCInProgress && !isModalClosed) handleGetVideoKycStatus();
            if (isVideoKYCRejected) dispatch(changeNiumForexScreen(NF_SCREENS.send_money));
          }}
        />
      ),
      'nium-fores retry-aadhar insta-modal center-modal',
      true
    );
  };

  const handleGetVideoKycStatus = async () => {
    if (txnId) {
      const response: any = await dispatch(getUserVideoKycStatus(txnId));
      if (response?.videoKycStatus === VERIFIED || response?.videoKycStatus === REVIEW_REQUIRED) {
        dispatch(getNiumForexUserDetails());
        dispatch(storeTransactionIdAction(txnId));
        dispatch(changeNiumForexScreen(NF_SCREENS.fund_transfer));
      } else {
        const isModalClosed = sessionStorage.getItem(NF_IS_VKYC_MODAL_CLOSED);
        if (response?.videoKycStatus === IN_PROGRESS && isModalClosed === 'true') {
          handleIssueWithVideoKycVerification(response?.videoKycStatus, true);
        } else {
          handleIssueWithVideoKycVerification(response?.videoKycStatus);
        }
      }
    }
    dispatch(disableLoader());
  };

  const handleGetTransactionDetails = async () => {
    if (txnId) {
      dispatch(activateLoader());
      const trData: any = await dispatch(
        getNiumForexTransactionDetails(isReferral ? transactionId : txnId)
      );
      if (trData?.data?.success) {
        const { created_at, cancellation_duration } = trData?.data?.data || '';
        const remainingTime = calculateRemainingTime(created_at, cancellation_duration);
        if (remainingTime) setTimeRemaining(remainingTime);
      }
      dispatch(disableLoader());
    }
  };

  return (
    <>
      <ParentContainer padding={[30, 0]}>
        <div className="navigationContainer">
          <img
            className="closeButton"
            onClick={handleCloseVideoKyc}
            src={CLOSE_POPUP_ICON}
            alt="close"
          />
        </div>
        <StepHeader
          heading={VIDEO_KYC?.heading}
          headerLogo={ID_CARD_FILL_ICON}
          stepNumber={stepperData?.stepNumber}
          totalSteps={totalSteps}
          subHeading={VIDEO_KYC?.keepPanCardHandy}
        />
        {timeRemaining.hours ? (
          <TimeContainer>
            <Label>{VIDEO_KYC?.validFor}</Label>
            <Value>
              <Timer
                hours={timeRemaining.hours || 0}
                minutes={timeRemaining.minutes || 0}
                seconds={timeRemaining.seconds || 0}
              />
            </Value>
          </TimeContainer>
        ) : null}
        <FormContainer marginTop={48}>
          <div className="kycInfo">
            <Row padding={[0, 0, 48, 0]}>
              <Col width={450}>
                <DisclaimerInfo margin={[0, 0, 0, 0]}>
                  <div className="image">
                    <img alt="location" src={CURRENT_LOCATION_PIN_ICON} />
                    <p className="info">
                      <strong className="bold">{VIDEO_KYC?.mustBeInIndia}</strong>
                      {VIDEO_KYC?.locationDisclaimer}
                    </p>
                  </div>
                </DisclaimerInfo>
              </Col>
            </Row>
            <Row padding={[0, 0, 24, 0]}>
              <Col width={450}>
                <Text>{VIDEO_KYC?.kycInfo}</Text>
              </Col>
            </Row>
            <CommonButton
              text={VIDEO_KYC?.startVideoverification}
              onClick={handleStartVideoKyc}
              width={450}
            />
          </div>
        </FormContainer>
      </ParentContainer>
    </>
  );
};

const mapStateToProps = (reduxState: IStore) => {
  const {
    rootReducer: {
      niumForexReducer: {
        transactionDetails: { transactionId },
        isReferral
      }
    }
  } = reduxState;

  return {
    transactionId,
    isReferral
  };
};

export default connect<any, any, any, IStore>(mapStateToProps, (dispatch: React.Dispatch<any>) => {
  return {
    activateLoader,
    disableLoader,
    getVideoKycCaptureLink,
    getUserVideoKycStatus,
    getNiumForexTransactionDetails,
    changeNiumForexScreen,
    storeTransactionIdAction,
    dispatch
  };
  //@ts-ignore
})(VideoKycScreen);
